import {
  AgencyDetail,
  Agency,
  Currency,
  AgencyStatusFilter,
  AgencyWithPagination,
  UpdateAgencySelfRequest
} from './Agency';
import { AgencyWebService, RestfulAgencyWebService } from 'ws/AgencyWebService';
import { CompanyMember } from 'core/companyMember/CompanyMember';
import { AdLogo } from 'core/adLogo/AdLogo';
import { Pageable } from 'ws/Pageable';
import { generateI18nOfSelectOptions } from 'utils/I18nUtils';
import {
  ChannelGroupWebService,
  RestfulChannelGroupWebService
} from 'ws/ChannelGroupWebService';
import { CampaignBidPriceSetting } from 'core/channelGroup/ChannelGroup';
import { defaultTo, keys } from 'lodash';
import { AdType } from 'core/rtbCampaign/RtbCampaign';

export interface AgencyManager {
  getRTBDefaultMinBudgetPerDay(currency: string): number;
  getRTBDefaultMinBudget(currency: string): number;
  getAgencies(addonFeatures?: string): Promise<Array<Agency>>;
  getAgenciesWithPagination(
    pageable: Pageable,
    searchString: string,
    addonFeatures?: string,
    status?: AgencyStatusFilter
  ): Promise<AgencyWithPagination>;
  archiveAgency(agencyId: number): Promise<void>;
  restoreAgency(agencyId: number): Promise<void>;
  getCreditLog(agencyId: number): Promise<any>;
  getBill(agencyId: number): Promise<any>;
  fetchAgency(
    agencyId: number,
    needChannelGroupsData?: boolean
  ): Promise<Agency>;
  updateAgency(request: AgencyDetail): Promise<number>;
  updateAgencySelf(
    agencyId: number,
    request: UpdateAgencySelfRequest
  ): Promise<number>;
  updateAgencyMargin(agencyId: number, agcPercent: number): Promise<void>;
  createAgency(request: AgencyDetail): Promise<number>;
  getMembers(agencyId: number): Promise<Array<CompanyMember>>;
  getAgenciesOptions(
    addonFeature?: string,
    extraFields?: string[],
    allEnable?: boolean
  ): Promise<Array<SelectOptions>>;
  getOemOptions(): Promise<Array<SelectOptions>>;
  payBill(billId: number): Promise<void>;
  updateRebate(
    agencyId: number,
    rebateData: {
      id: string | number;
      effectDate: string;
      rate: string | number;
    }
  ): Promise<void>;
  getAdLogo(agencyId: number): Promise<AdLogo>;
  getAgencyName(agencyId: number): Promise<string | null>;
  mergeChannelGroupBidPriceSetting(agencyDetail: AgencyDetail): Promise<void>;
  getAgencyPublishers(agencyId: number): Promise<string[]>;
}

const minBudgetPerDayMap = {
  [Currency.USD]: 10,
  [Currency.IDR]: 150000,
  [Currency.NTD]: 300,
  [Currency.JPY]: 1500
};

const minBudgetMap = {
  [Currency.USD]: 3,
  [Currency.IDR]: 50000,
  [Currency.NTD]: 100,
  [Currency.JPY]: 500
};

export class DefaultAgencyManager implements AgencyManager {
  constructor(
    private webService: AgencyWebService = new RestfulAgencyWebService(),
    private channelGroupWebService: ChannelGroupWebService = new RestfulChannelGroupWebService()
  ) {}

  getRTBDefaultMinBudgetPerDay(currency: string): number {
    return minBudgetPerDayMap[currency];
  }

  getRTBDefaultMinBudget(currency: string): number {
    return minBudgetMap[currency];
  }

  getAgencies(addonFeature?: string): Promise<Array<Agency>> {
    return this.webService.getAgencies(addonFeature);
  }

  getAgenciesWithPagination(
    pageable: Pageable,
    searchString: string,
    addonFeatures?: string,
    status?: AgencyStatusFilter
  ): Promise<AgencyWithPagination> {
    return this.webService.getAgenciesWithPagination(
      pageable,
      searchString,
      addonFeatures,
      status
    );
  }

  async archiveAgency(agencyId: number): Promise<void> {
    return this.webService.archiveAgency(agencyId);
  }

  async restoreAgency(agencyId: number): Promise<void> {
    return this.webService.restoreAgency(agencyId);
  }

  async getCreditLog(agencyId: number): Promise<any> {
    return this.webService.getCreditLog(agencyId);
  }

  async getBill(agencyId: number): Promise<any> {
    return this.webService.getBill(agencyId);
  }

  async fetchAgency(
    agencyId: number,
    needChannelGroupsData = false
  ): Promise<Agency> {
    const agency = await this.webService.getAgency(agencyId);

    if (!needChannelGroupsData) {
      return agency;
    }

    await this.mergeChannelGroupBidPriceSetting(agency.detail);
    return agency;
  }

  // 送出前把 addonFeatures 各群組內 value 為 false 的 item 過濾掉，只保留已啟用的
  private withEnabledAddonFeatures(request: AgencyDetail): AgencyDetail {
    const { addonFeatures } = request;
    if (!addonFeatures || typeof addonFeatures !== 'object') {
      return request;
    }
    const filtered = Object.fromEntries(
      Object.entries(addonFeatures).map(([group, items]) => {
        if (!items || typeof items !== 'object' || Array.isArray(items)) {
          return [group, items];
        }
        return [
          group,
          Object.fromEntries(
            Object.entries(items as Record<string, unknown>).filter(
              ([, value]) => value !== false
            )
          )
        ];
      })
    );
    return { ...request, addonFeatures: filtered };
  }

  async updateAgency(request: AgencyDetail): Promise<number> {
    return this.webService.updateAgency(this.withEnabledAddonFeatures(request));
  }

  async updateAgencySelf(
    agencyId: number,
    request: UpdateAgencySelfRequest
  ): Promise<number> {
    return this.webService.updateAgencySelf(agencyId, request);
  }

  async updateAgencyMargin(
    agencyId: number,
    agcPercent: number
  ): Promise<void> {
    return this.webService.updateAgencyMargin(agencyId, agcPercent);
  }

  async createAgency(request: AgencyDetail): Promise<number> {
    return this.webService.createAgency(this.withEnabledAddonFeatures(request));
  }

  async getMembers(agencyId: number): Promise<Array<CompanyMember>> {
    return this.webService.getAgencyMember(agencyId);
  }

  async getAgenciesOptions(
    addonFeature?: undefined,
    extraFields?: string[],
    allAddonEnable?: undefined
  ): Promise<Array<SelectOptions>>;
  async getAgenciesOptions(
    addonFeature: string,
    extraFields?: string[],
    allAddonEnable?: boolean
  ): Promise<Array<SelectOptions>>;
  async getAgenciesOptions(
    addonFeature?: string,
    extraFields?: string[],
    allAddonEnable = true
  ): Promise<Array<SelectOptions>> {
    const options = await this.webService.getAgenciesOptions(
      addonFeature,
      extraFields,
      allAddonEnable
    );
    options.forEach((option) =>
      generateI18nOfSelectOptions(option, 'agency.name')
    );
    return options;
  }
  async getOemOptions(): Promise<Array<SelectOptions>> {
    return this.webService.getOemOptions();
  }

  async updateRebate(
    agencyId: number,
    rebateData: {
      id: string | number;
      effectDate: string;
      rate: string | number;
    }
  ): Promise<void> {
    return this.webService.updateRebate(agencyId, rebateData);
  }

  async payBill(billId: number): Promise<void> {
    return this.webService.payBill(billId);
  }

  async getAdLogo(agencyId: number): Promise<AdLogo> {
    return this.webService.getAdLogo(agencyId);
  }

  async getAgencyName(agencyId: number): Promise<string | null> {
    const name = await this.webService.getAgencyName(agencyId);
    if (name) {
      generateI18nOfSelectOptions(
        { label: name, value: agencyId },
        'agency.name'
      );
    }
    return name;
  }

  async getAgencyPublishers(agencyId: number): Promise<string[]> {
    return this.webService.getAgencyPublishers(agencyId);
  }

  async mergeChannelGroupBidPriceSetting(
    agencyDetail: AgencyDetail
  ): Promise<void> {
    const adTypeOrder: string[] = keys(AdType).map((key) => AdType[key]);

    // 這裡需要「所有渠道」的 bid price，含已封存的：後端會比對 DB 既有值與前端送出的
    // DTO，把差異寫進 audit log。若前端把封存渠道的 bid price 濾掉後送出存進 DB，之後
    // 任何更新時後端從 DB 撈出原 agency，會經 AgencyPropertyConverter 把後端寫好的
    // 預設 bid price 補回去；如此一來不論前端有沒有實際修改，都會被判定為有差異而誤寫
    // 進 audit log。
    // ECOPD-5566 起 getChannelGroups 本來就回傳全部渠道（含 ARCHIVED），
    // 不再需要額外參數。
    const channelGroups =
      await this.channelGroupWebService.getChannelGroups(true);
    const channelGroupBidPriceSettings = channelGroups.reduce(
      (acc, channelGroup) => {
        return [...acc, ...channelGroup.property.addonProps.campaignBidPrice];
      },
      [] as CampaignBidPriceSetting[]
    );
    const agencyCampaignBidPriceSettings = defaultTo(
      agencyDetail.addonProps.campaignBidPrice,
      []
    );
    // merge channel group bid price setting to agency bid price setting
    channelGroupBidPriceSettings.forEach((channelGroupBidPriceSetting) => {
      const bidPriceSetting = agencyCampaignBidPriceSettings.find(
        (agencyCampaignBidPriceSetting) => {
          if (agencyCampaignBidPriceSetting.adTypeId) {
            return (
              agencyCampaignBidPriceSetting.adTypeId ===
              channelGroupBidPriceSetting.adTypeId
            );
          }
          return (
            agencyCampaignBidPriceSetting.type ===
            channelGroupBidPriceSetting.type
          );
        }
      );
      if (!bidPriceSetting) {
        if (
          channelGroupBidPriceSetting.adTypeId ||
          channelGroupBidPriceSetting.type
        ) {
          agencyCampaignBidPriceSettings.push(channelGroupBidPriceSetting);
        }
      } else {
        // merge optimize settings
        Object.keys(channelGroupBidPriceSetting.autoBidCap).forEach(
          (optimize) => {
            if (!bidPriceSetting.autoBidCap[optimize]) {
              bidPriceSetting.autoBidCap[optimize] =
                channelGroupBidPriceSetting.autoBidCap[optimize];
            }
          }
        );
        Object.keys(channelGroupBidPriceSetting.bidFloor).forEach(
          (optimize) => {
            if (!bidPriceSetting.bidFloor[optimize]) {
              bidPriceSetting.bidFloor[optimize] =
                channelGroupBidPriceSetting.bidFloor[optimize];
            }
          }
        );
      }
    });
    // remove invalid bid price setting
    const validBidPriceType = channelGroupBidPriceSettings.map(
      (bidPrice) => bidPrice.adTypeId || bidPrice.type
    );
    agencyDetail.addonProps.campaignBidPrice = agencyCampaignBidPriceSettings
      .filter((bidPriceSetting) =>
        bidPriceSetting.adTypeId
          ? validBidPriceType.includes(bidPriceSetting.adTypeId)
          : validBidPriceType.includes(bidPriceSetting.type)
      )
      .sort((a, b) => {
        if (a.adTypeId && b.adTypeId) {
          return a.adTypeId - b.adTypeId;
        }
        if (a.adTypeId && !b.adTypeId) {
          return 1;
        }
        if (!a.adTypeId && b.adTypeId) {
          return -1;
        }
        return adTypeOrder.indexOf(a.type) - adTypeOrder.indexOf(b.type);
      });
  }
}
